import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, Typography } from "@mui/material";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { type STEPS, useStepNavigation } from "@/hooks/useStepNavigation";
import { formatDateRange, formatGuests, getCampsiteTypeLabel } from "@/utils/booking";

interface BookingStepProps {
  name: keyof typeof STEPS;
  label: string;
  value: string;
}

function BookingStep({ name, label, value }: BookingStepProps) {
  const { isStepAvailable, goToStep, currentStep } = useStepNavigation();
  const isClickable = isStepAvailable(name);
  const handleClick = () => {
    if (isClickable) {
      goToStep(name);
    }
  };

  const isActive = name === currentStep;

  return (
    <Box
      onClick={handleClick}
      sx={{
        flex: 1,
        height: 64,
        minWidth: 0,
        borderRadius: 10,
        bgcolor: "background.paper",
        border: 1,
        borderColor: isActive ? "primary.main" : "divider",
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        opacity: isActive || value ? 1 : 0.5,
        px: 3,
        py: 1.5,
        cursor: isClickable ? "pointer" : "default",
        transition: "all 0.2s ease-in-out",
        boxShadow: isActive ? 10 : "none",
        "&:hover":
          isClickable && !isActive
            ? {
                transform: "translateY(-2px)",
                boxShadow: 10,
              }
            : {},
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontWeight: 600,
          transition: "font-size 0.1s ease-in-out",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
          textTransform: "capitalize",
          maxHeight: value ? 24 : 0,
          opacity: value ? 1 : 0,
          transition:
            "max-height 0.25s ease-in-out, opacity 0.25s ease-in-out, margin 0.25s ease-in-out",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function SearchProgressIndicator() {
  const { _, i18n } = useLingui();
  const { state } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { goToStep, isStepAvailable } = useStepNavigation();

  const steps: { name: keyof typeof STEPS; label: string; value: string }[] = [
    {
      name: "dates",
      label: _(msg`When?`),
      value: formatDateRange([state.dateFrom!, state.dateUntil!], i18n.locale),
    },
    {
      name: "accommodation",
      label: _(msg`How?`),
      value: getCampsiteTypeLabel(state.selectedCampsiteType, campgroundData.campsite_types),
    },
    {
      name: "guests",
      label: _(msg`Who?`),
      value: formatGuests(state.selectedPersons, campgroundData.campground?.persons || []),
    },
  ];

  return (
    <Box
      sx={{
        mb: 3,
        width: "100%",
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        borderRadius: 10,
        p: 0.625,
        display: "flex",
        gap: 1.375,
        alignItems: "center",
      }}
    >
      {steps.map((step) => (
        <BookingStep key={step.name} name={step.name} label={step.label} value={step.value} />
      ))}
      <IconButton
        sx={{
          width: 64,
          height: 64,
          borderRadius: 10,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          "&:hover": {
            bgcolor: "primary.dark",
          },
        }}
        onClick={() => goToStep("results")}
        disabled={!isStepAvailable("results")}
      >
        <SearchIcon />
      </IconButton>
    </Box>
  );
}
